/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LicensingModule
 */

import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    Type,
} from '@angular/core';

import {
    BehaviorSubject,
    Subscription,
    timer,
} from 'rxjs';

import {
    AviDataGridFieldVisibility,
    IAviDataGridConfig,
} from 'ng/modules/data-grid/components/avi-data-grid/avi-data-grid.types';

import {
    AlertType,
    ILicense,
    ILicenseAddAlert,
} from 'ng/modules/licensing/licensing.types';

import { AviDatePipe } from 'ng/modules/core/pipes';
import { AviNotificationType } from 'ng/modules/notification/avi-notification.types';
import { DialogService } from 'ng/modules/core/services/dialog.service';
import { InitialDataService } from 'ng/modules/core/services/initial-data/initial-data.service';
import { L10nService } from '@vmw/ngx-vip';
import { NotificationService } from 'ng/modules/core/services/notification.service';
import { SchemaService } from 'ajs/modules/core/services';
import * as globalL10n from 'global-l10n';
import moment from 'moment';

import { AVI_LOAD_BALANCER_LICENSING_GUIDE } from '../../constants';
import { LicenseAddPanelModalComponent } from '../license-add-panel-modal';
import { LicenseDeleteDialogComponent } from '../license-delete-dialog';
import { LicensingPageStore } from '../licensing-page.store';
import * as l10n from './license-list.l10n';
import './license-list.component.less';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const LICENSE_TYPE_TRIAL = 'Trial';
const DELETE_LICENSES_DIALOG_ID = 'license-delete-dialog';
const ADD_LICENSE_DIALOG_ID = 'add-license-dialog';

/**
 * Max timer delay for auto-hiding legacy license alert (60 minutes).
 * Timer is only set when expiry is within this limit to avoid long-running timers.
 */
const MAX_ALERT_TIMER_DELAY_MS = 60 * 60 * 1000;

/**
 * @description Component to display and manage license list with data grid.
 * @author Shubam Bindal
 */
@Component({
    selector: 'license-list',
    templateUrl: './license-list.component.html',
})
export class LicenseListComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    /**
     * Array of licenses to display in the grid.
     */
    @Input()
    public licenses: ILicense[] = [];

    @Input()
    public isBusy = false;

    /**
     * Whether to show the refresh licenses button (for ENTERPRISE tier).
     */
    @Input()
    public showRefreshButton = false;

    /**
     * Whether to show the add license button.
     */
    @Input()
    public showAddLicenseButton = false;

    /**
     * Legacy license grace period date string from API.
     */
    @Input()
    public legacyLicenseGracePeriod?: string;

    @Input()
    public isOnPremLicensingEnabled?: boolean;

    /**
     * Event emitted when licenses need to be reloaded.
     */
    @Output()
    public onReload = new EventEmitter<void>();

    /**
     * Event emitted when refresh licenses button is clicked.
     */
    @Output()
    public onRefresh = new EventEmitter<void>();

    public controllerTimeDifference = 0;

    /**
     * Grid configuration for license list.
     */
    public licenseListGridConfig: IAviDataGridConfig;

    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly aviLoadBalancerLicensingKbUrl = AVI_LOAD_BALANCER_LICENSING_GUIDE;

    /**
     * Observable for legacy license alert visibility.
     */
    public readonly showLegacyLicenseAlert$ = new BehaviorSubject<boolean>(false);

    /**
     * Alert message for legacy license expiry.
     */
    public legacyLicenseAlertMessage = '';

    /**
     * Subscription for grace period expiry timer.
     */
    private gracePeriodTimerSubscription: Subscription;

    constructor(
        private readonly licensingPageStore: LicensingPageStore,
        private readonly l10nService: L10nService,
        private readonly initialDataService: InitialDataService,
        private readonly schemaService: SchemaService,
        private readonly dialogService: DialogService,
        private readonly notificationService: NotificationService,
        private readonly aviDate: AviDatePipe,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * @override
     */
    public ngOnInit(): void {
        this.controllerTimeDifference = this.initialDataService?.controllerTimeDifference ?? 0;
    }

    /**
     * @override
     */
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.legacyLicenseGracePeriod) {
            this.updateLegacyLicenseAlert();
        }

        if (changes.isOnPremLicensingEnabled && this.licenseListGridConfig) {
            this.setLicenseListGridConfig();
        }
    }

    /**
     * @override
     */
    public ngAfterViewInit(): void {
        this.setLicenseListGridConfig();
    }

    /**
     * @override
     */
    public ngOnDestroy(): void {
        this.gracePeriodTimerSubscription?.unsubscribe();
    }

    public onRefreshLicenses(): void {
        this.onRefresh.emit();
    }

    /**
     * Open the add license dialog.
     */
    public openAddLicenseModal(): void {
        if (this.dialogService.has(ADD_LICENSE_DIALOG_ID)) {
            this.dialogService.remove(ADD_LICENSE_DIALOG_ID);
        }

        this.dialogService.add({
            id: ADD_LICENSE_DIALOG_ID,
            component: LicenseAddPanelModalComponent as Type<Component>,
            componentProps: {
                onClose: () => {
                    this.dialogService.remove(ADD_LICENSE_DIALOG_ID);
                },
                onLicenseAdded: (alert: ILicenseAddAlert) => {
                    this.dialogService.remove(ADD_LICENSE_DIALOG_ID);

                    if (alert.type === AlertType.success) {
                        this.onReload.emit();
                    }
                },
            },
        });
    }

    /**
     * Set grid configuration with fields and actions.
     * @protected
     */
    private setLicenseListGridConfig(): void {
        this.licenseListGridConfig = {
            fields: [
                {
                    label: this.l10nService.getMessage(l10nKeys.columnTitleDescription),
                    id: 'license_name',
                    transform: (row: ILicense) => row.license_name ?? '',
                    visibility: AviDataGridFieldVisibility.OPTIONAL,
                },
                {
                    label: this.l10nService.getMessage(l10nKeys.columnTitleType),
                    id: 'license_type',
                    transform: (row: ILicense) => row.license_type ?? '',
                },
                {
                    label: this.l10nService.getMessage(l10nKeys.columnTitleLicenseId),
                    id: 'license_id',
                    transform: (row: ILicense) => row.license_id ?? '',
                },
                {
                    label: this.l10nService.getMessage(l10nKeys.columnTitleTier),
                    id: 'tier',
                    transform: (row: ILicense) => this.schemaService
                        .getEnumValue('LicenseTierType', row.tier_type)?.value ?? '',
                    visibility: AviDataGridFieldVisibility.OPTIONAL,
                },
                {
                    label: this.l10nService.getMessage(l10nKeys.columnTitleServiceUnits),
                    id: 'serviceCores',
                    transform: (row: ILicense) => row.service_cores ?? 0,
                },
                {
                    label: this.l10nService.getMessage(l10nKeys.seVCpus),
                    id: 'cores',
                    transform: (row: ILicense) => row.cores ?? 0,
                    visibility: AviDataGridFieldVisibility.OPTIONAL,
                }, {
                    label: this.l10nService.getMessage(l10nKeys.columnTitleSeSockets),
                    id: 'sockets',
                    transform: (row: ILicense) => row.sockets ?? 0,
                    visibility: AviDataGridFieldVisibility.OPTIONAL,
                },
                {
                    label: this.l10nService.getMessage(l10nKeys.columnTitleExpiry),
                    id: 'valid_until',
                    transform: (row: ILicense) => this.transformValidUntil(row),
                },
            ],
            layout: {
                hideCheckboxes: this.isOnPremLicensingEnabled,
            },
            singleactions: this.isOnPremLicensingEnabled ? [] : [{
                label: this.l10nService.getMessage(l10nKeys.actionTitleDelete),
                onClick: (row: ILicense) => this.deleteLicenses([row]),
                disabled: (row: ILicense) => this.shouldDisableRow(row),
            }],
            multipleactions: this.isOnPremLicensingEnabled ? [] : [{
                label: this.l10nService.getMessage(l10nKeys.actionTitleDelete),
                onClick: (rows: ILicense[]) => this.deleteLicenses(rows),
                disabled: (rows: ILicense[]) => this.shouldDisableMultiActionDelete(rows),
            }],
            searchKeys: [
                'license_name',
                'license_type',
                'license_id',
                'tier_type',
                'service_cores',
                'cores',
                'sockets',
            ],
        };
    }

    /**
     * Transform valid_until date to display format.
     * @param license - License object
     * @returns Formatted date string or expired label
     * @protected
     */
    private transformValidUntil(license: ILicense): string {
        if (!license.valid_until) {
            return '';
        }

        const validUntil = moment(license.valid_until).add(this.controllerTimeDifference, 'seconds');

        if (validUntil.isBefore()) {
            return this.l10nService.getMessage(l10nKeys.expiredLabel);
        }

        return validUntil.format('ll');
    }

    /**
     * Delete licenses, open dialog and take confirmation from the user.
     * @protected
     */
    private deleteLicenses(rows: ILicense[]): void {
        this.dialogService.add({
            id: DELETE_LICENSES_DIALOG_ID,
            component: LicenseDeleteDialogComponent as Type<Component>,
            componentProps: {
                licenses: rows,
                onDelete: () => {
                    this.executeDeleteLicenses(rows);
                    this.dialogService.remove(DELETE_LICENSES_DIALOG_ID);
                },
                onClose: () => this.dialogService.remove(DELETE_LICENSES_DIALOG_ID),
            },
        });
    }

    /**
     * Execute deletion of licenses after user confirmation.
     * @param rows - Array of licenses to delete
     * @protected
     */
    private async executeDeleteLicenses(rows: ILicense[]): Promise<void> {
        try {
            const deletePromises = rows.map((license: ILicense) =>
                this.licensingPageStore.deleteLicense(license.license_id));

            await Promise.all(deletePromises);
            this.onLicenseDeleteSuccess(rows);
        } catch (error) {
            const errorMessage = error?.data?.error ||
                'Failed to delete license(s). Please try again.';

            this.notificationService.addSimpleNotification(
                errorMessage,
                '',
                AviNotificationType.ERROR,
            );
        }
    }

    /**
     * Determine if actions for a specific license row should be disabled.
     * Actions are disabled if the license is Trial or if component is busy.
     * @protected
     */
    private shouldDisableRow(row: ILicense): boolean {
        if (this.isBusy) {
            return true;
        }

        return row?.license_id === LICENSE_TYPE_TRIAL;
    }

    /**
     * Disable multi action delete while license is Trial or component is busy.
     * @protected
     */
    private shouldDisableMultiActionDelete(rows: ILicense[]): boolean {
        if (this.isBusy) {
            return true;
        }

        return rows.some(row => row.license_id === LICENSE_TYPE_TRIAL);
    }

    /**
     * Alert for license delete success.
     * @protected
     */
    private onLicenseDeleteSuccess(rows: ILicense[]): void {
        const notificationMsg = this.l10nService.getMessage(
            l10nKeys.licensesDeletedMsg,
            [rows.length],
        );

        this.notificationService.addSimpleNotification(
            notificationMsg,
            '',
            AviNotificationType.SUCCESS,
        );

        this.onReload.emit();
    }

    /**
     * Update the legacy license alert based on the grace period.
     * Show alert if grace period is today or in the future.
     */
    private updateLegacyLicenseAlert(): void {
        this.gracePeriodTimerSubscription?.unsubscribe();

        if (!this.legacyLicenseGracePeriod) {
            this.showLegacyLicenseAlert$.next(false);

            return;
        }

        const gracePeriodDate = moment.utc(this.legacyLicenseGracePeriod);
        const now = moment.utc();

        if (gracePeriodDate.isAfter(now)) {
            const startOfToday = now.clone().startOf('day');
            const daysRemaining = gracePeriodDate.diff(startOfToday, 'days');
            const formattedDate = this.aviDate.transform(`${this.legacyLicenseGracePeriod}+00:00`);

            this.legacyLicenseAlertMessage = this.l10nService.getMessage(
                l10nKeys.legacyLicenseExpiryAlert,
                [formattedDate, daysRemaining],
            );
            this.showLegacyLicenseAlert$.next(true);

            const msUntilExpiry = gracePeriodDate.diff(moment.utc());

            if (msUntilExpiry > 0 && msUntilExpiry <= MAX_ALERT_TIMER_DELAY_MS) {
                this.gracePeriodTimerSubscription = timer(msUntilExpiry).subscribe(
                    () => this.showLegacyLicenseAlert$.next(false),
                );
            }
        } else {
            this.showLegacyLicenseAlert$.next(false);
        }
    }
}
